import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { IVisibilityCandidate } from "./IVisibilityCandidate";

export class ProfileRule {
  private myIsTrader: boolean;

  private myTypeMap: Map<CubiqTS.OrderType, boolean>;
  private myAllTypes: boolean;

  private myAllBranches: boolean;
  private myBranchMap: any;

  private myAllSalesGroups: boolean = false;
  private mySalesGroupMap: any;

  private myAllPairs: boolean = false;
  private myPairMap: any;

  private myAllTraderGroups: boolean = false;
  private myTraderGroupMap: any;
  private myMemberOfTraderGroupMap: Set<string>;

  constructor(
    rule: CubiqTS.UserVisibilityRuleImpl,
    isTrader: boolean,
    userTraderGroups: Array<string>,
  ) {
    this.myIsTrader = isTrader;
    this.initializeRule(rule, userTraderGroups);
  }

  private initializeRule(
    rule: CubiqTS.UserVisibilityRuleImpl,
    userTraderGroups: Array<string>,
  ): void {
    this.myAllTypes = ProfileRule.isAllTypes(rule);
    if (!this.myAllTypes) {
      this.myTypeMap = ProfileRule.createTypeMap(rule);
    }
    this.myAllBranches = ProfileRule.isAllBranches(rule);
    if (!this.myAllBranches) {
      this.myBranchMap = ProfileRule.createBranchMap(rule);
    }
    this.myAllSalesGroups = ProfileRule.isAllSalesGroups(rule);
    if (!this.myAllSalesGroups) {
      this.mySalesGroupMap = ProfileRule.createSalesGroupMap(rule);
    }

    if (this.myIsTrader) {
      this.myAllPairs = ProfileRule.isAllPairs(rule);
      if (!this.myAllPairs) {
        this.myPairMap = ProfileRule.createPairMap(rule);
      }
      this.myAllTraderGroups = ProfileRule.isAllTraderGroups(rule);
      if (!this.myAllTraderGroups) {
        this.myTraderGroupMap = ProfileRule.createTraderGroupMap(rule);
      }
      this.myMemberOfTraderGroupMap = new Set(userTraderGroups);
    }
  }
  /**
   * per flex, relaxed in absence of fields
   */
  public isOpsIntVisible(candidate: IVisibilityCandidate): boolean {
    if (
      candidate.orderType != null &&
      !this.myAllTypes &&
      !this.myTypeMap.get(candidate.orderType)
    ) {
      return false;
    }
    if (
      candidate.branchID != null &&
      !this.myAllBranches &&
      !this.myBranchMap[candidate.branchID]
    ) {
      return false;
    }
    if (
      candidate.salesGroupID != null &&
      !this.myAllSalesGroups &&
      !this.mySalesGroupMap[candidate.salesGroupID]
    ) {
      return false;
    }
    if (this.myIsTrader) {
      if (candidate.pairID != null && !this.myAllPairs && !this.myPairMap[candidate.pairID]) {
        return false;
      }
      if (!this.myAllTraderGroups) {
        if (
          candidate.traderGroupID != null &&
          !this.myTraderGroupMap[candidate.traderGroupID] &&
          (candidate.targetGroupID == null ? true : !this.myTraderGroupMap[candidate.targetGroupID])
        ) {
          return false;
        }
      }
    }
    return true;
  }

  public isVisible(candidate: IVisibilityCandidate): boolean {
    if (candidate.lockedToMe) return true;

    if (!this.myAllTypes && !this.myTypeMap.get(candidate.orderType)) {
      return false;
    }
    if (!this.myAllBranches && !this.myBranchMap[candidate.branchID]) {
      return false;
    }
    if (!this.myAllSalesGroups && !this.mySalesGroupMap[candidate.salesGroupID]) {
      return false;
    }
    if (this.myIsTrader) {
      if (!this.myAllPairs && !this.myPairMap[candidate.pairID]) {
        return false;
      }
      if (!this.myAllTraderGroups) {
        if (
          !this.myTraderGroupMap[candidate.traderGroupID] &&
          (candidate.targetGroupID == null ? true : !this.myTraderGroupMap[candidate.targetGroupID])
        ) {
          return false;
        }
      }
    }
    return true;
  }

  public isInboxVisible(candidate: IVisibilityCandidate): boolean {
    if (!this.myAllTypes && !this.myTypeMap.get(candidate.orderType)) {
      return false;
    }
    if (!this.myAllBranches && !this.myBranchMap[candidate.branchID]) {
      return false;
    }
    if (!this.myAllSalesGroups && !this.mySalesGroupMap[candidate.salesGroupID]) {
      return false;
    }
    if (this.myIsTrader) {
      if (!this.myAllPairs && !this.myPairMap[candidate.pairID]) {
        return false;
      }

      if (
        !(
          candidate.interventionState &&
          (this.myAllTraderGroups ||
            (this.myTraderGroupMap && this.myTraderGroupMap[candidate.traderGroupID]))
        ) &&
        (candidate.targetGroupID == null
          ? true
          : !this.myMemberOfTraderGroupMap.has(candidate.targetGroupID))
      ) {
        return false;
      }
    }

    return true;
  }

  public isBmarkVisible(candidate: IVisibilityCandidate): boolean {
    if (candidate.lockedToMe) {
      return true;
    }
    if (!this.myAllTypes && !this.myTypeMap.get(candidate.orderType)) {
      return false;
    }
    if (!this.myAllBranches && !this.myBranchMap[candidate.branchID]) {
      return false;
    }
    if (!this.myAllSalesGroups && !this.mySalesGroupMap[candidate.salesGroupID]) {
      return false;
    }
    if (this.myIsTrader) {
      // Bmark CcyPair visibility
      if (!this.myAllPairs) {
        // bmark allocation
        if (candidate.bmarkCheckAllocationOnly && candidate.bmarkAllocationCcyPair) {
          if (!this.myPairMap[candidate.bmarkAllocationCcyPair]) {
            return false;
          }
          // bmark order
        } else if (Array.isArray(candidate.bmarkCcyPairs) && candidate.bmarkCcyPairs.length > 0) {
          let isAnyMyPair: boolean = false;
          for (let aloCcyPair of candidate.bmarkCcyPairs) {
            isAnyMyPair = isAnyMyPair || !!this.myPairMap[aloCcyPair];
          }
          if (!isAnyMyPair) {
            return false;
          }
        } else {
          return false;
        }
      }
      // Bmark TraderGroup visibility
      if (!this.myAllTraderGroups) {
        // bmark allocation
        if (candidate.bmarkCheckAllocationOnly && candidate.bmarkAllocationTraderGroupID != null) {
          if (!this.myTraderGroupMap[candidate.bmarkAllocationTraderGroupID]) {
            return false;
          }
          // bmark order
        } else if (
          Array.isArray(candidate.bmarkTraderGroupIDs) &&
          candidate.bmarkTraderGroupIDs.length > 0
        ) {
          let belongsToTG: boolean = false;
          for (let traderGroupId of candidate.bmarkTraderGroupIDs) {
            if (this.myTraderGroupMap[traderGroupId]) {
              belongsToTG = true;
            }
          }
          if (!belongsToTG) {
            return false;
          }
        } else {
          return false;
        }
      }
    }
    return true;
  }

  // used for fixing rate events (for now)
  public isCcyPairVisible(ccyPair: string): boolean {
    if (typeof ccyPair === "string" && ccyPair.length === 6) {
      return this.myAllPairs || this.myPairMap[ccyPair];
    }
    return false;
  }

  private static isAllTypes(rule: CubiqTS.UserVisibilityRuleImpl): boolean {
    return rule.algoFg && rule.benchmarkFg && rule.limitFg;
  }

  private static createTypeMap(
    rule: CubiqTS.UserVisibilityRuleImpl,
  ): Map<CubiqTS.OrderType, boolean> {
    const retMap: Map<CubiqTS.OrderType, boolean> = new Map();

    if (rule.algoFg) {
      retMap.set(CubiqTS.OrderType.ALGO, true);
    }
    if (rule.benchmarkFg) {
      retMap.set(CubiqTS.OrderType.BENCHMARK, true);
      retMap.set(CubiqTS.OrderType.BMALGO, true);
    }
    if (rule.limitFg) {
      retMap.set(CubiqTS.OrderType.LIMIT_CALL, true);
      retMap.set(CubiqTS.OrderType.LIMIT_SL, true);
      retMap.set(CubiqTS.OrderType.LIMIT_TP, true);
      // retMap.set(CubiqTS.OrderType.MARKET, true);
    }
    return retMap;
  }

  private static isAllBranches(rule: CubiqTS.UserVisibilityRuleImpl): any {
    return rule.branches.length == 0;
  }

  private static createBranchMap(rule: CubiqTS.UserVisibilityRuleImpl): any {
    const retMap: any = {};
    rule.branches.forEach((branchID) => {
      retMap[branchID] = true;
    });
    return retMap;
  }

  private static isAllSalesGroups(rule: CubiqTS.UserVisibilityRuleImpl): any {
    return rule.salesGroups.length == 0;
  }

  private static createSalesGroupMap(rule: CubiqTS.UserVisibilityRuleImpl): any {
    const retMap: any = {};
    rule.salesGroups.forEach((groupID) => {
      retMap[groupID.toNumber()] = true;
    });
    return retMap;
  }

  private static isAllTraderGroups(rule: CubiqTS.UserVisibilityRuleImpl): any {
    return rule.traderGroups.length == 0;
  }

  private static createTraderGroupMap(rule: CubiqTS.UserVisibilityRuleImpl): any {
    const retMap: any = {};
    rule.traderGroups.forEach((groupID) => {
      retMap[groupID.toNumber()] = true;
    });
    return retMap;
  }

  private static isAllPairs(rule: CubiqTS.UserVisibilityRuleImpl): any {
    return rule.currencyPairs.length == 0;
  }

  private static createPairMap(rule: CubiqTS.UserVisibilityRuleImpl): any {
    const retMap: any = {};
    rule.currencyPairs.forEach((pairId) => {
      retMap[pairId] = true;
    });
    return retMap;
  }
}
